import React, { useMemo, useState } from "react";

import { orderBy } from "lodash";

import useGetPortfolioPlanClassificationById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanClassificationById";
import { PortfolioPlanClassification } from "@fartherfinance/frontend/api/Investment/request/getPortfolioPlanAssignmentClassificationById";
import { AssetClassCategory } from "@fartherfinance/frontend/api/PerformanceGroups/requests/getHoldings";
import {
  HookResult,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import AssetAllocationPieChart, {
  AssetClassification,
} from "@src/multiCustodian/components/Portfolios/AssetAllocationPieChart/AssetAllocationPieChart";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useApplicationMode from "@src/multiCustodian/hooks/useApplicationMode";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import ItemWithExpandCollapseContent from "@src/sharedComponents/ItemWithExpandCollapseContent/ItemWithExpandCollapseContent";
import { TabContext, TabPanel } from "@src/sharedComponents/Tabs/Tabs";
import { ToggleV2 } from "@src/sharedComponents/Toggle/v2/TwoTextItemToggle";

import ProjectedReturnsGraph from "./ProjectedReturnsGraph/ProjectedReturnsGraph";

import styles from "./Charts.module.css";

interface Props {
  portfolioPlanId?: PortfolioPlanId;
  classification?: ChartLabel[] | undefined;
  isLoadingIncomingClassification?: boolean;
  hasErrorIncomingClassification?: boolean;
}

type ChartLabel = {
  label: AssetClassCategory;
  value: number;
};

const Tabs = [{ key: "Asset Allocation", label: "Asset Allocation" } as const];

type TabKeys = typeof Tabs[number]["key"];

const getAllocations = (
  classification: PortfolioPlanClassification["data"]
) => {
  const allKeys = Object.keys(classification);
  const mappedValues = allKeys.map((label) => ({
    label: AssetClassCategory.parse(label),
    value: classification[label].weight.mul(100).toNumber(),
  }));

  return orderBy(mappedValues, ["value"], ["desc"]);
};

const PortfolioDetailsSideBarChartsSection: React.FC<Props> = ({
  portfolioPlanId = null,
  classification,
  isLoadingIncomingClassification,
  hasErrorIncomingClassification,
}) => {
  const [expandCharts, setExpandCharts] = useState<boolean>(true);

  const [selectedChart, setSelectedChart] =
    useState<TabKeys>("Asset Allocation");

  const { isAdvisorMode } = useApplicationMode();
  const clientAuth = useRequestAuth();
  const advisorAuth = useAdvisorRequestAuth();
  const auth = isAdvisorMode ? advisorAuth : clientAuth;

  const {
    data: portfolioPlanClassification,
    isLoading: isClassLoading,
    hasError: classHasError,
  } = useGetPortfolioPlanClassificationById(portfolioPlanId, auth);

  const allLabels: ChartLabel[] = useMemo(() => {
    if (portfolioPlanId === null) {
      if (classification === undefined) {
        return [];
      }

      return classification;
    }

    if (portfolioPlanClassification === undefined) {
      return [];
    }

    return getAllocations(portfolioPlanClassification);
  }, [portfolioPlanId, classification, portfolioPlanClassification]);

  const isLoading =
    isLoadingIncomingClassification ||
    (portfolioPlanId !== null && isClassLoading);

  const chartData: HookResult<AssetClassification[]> = useMemo(() => {
    if (isLoading) {
      return {
        isLoading: true,
        hasError: false,
        data: undefined,
      };
    }
    if (hasErrorIncomingClassification || classHasError) {
      return {
        isLoading: false,
        hasError: true,
        error: new Error("Unknown"),
        data: undefined,
      };
    }
    return {
      data: allLabels,
      hasError: false,
      isLoading: false,
    };
  }, [isLoading, hasErrorIncomingClassification, classHasError, allLabels]);

  return (
    <ItemWithExpandCollapseContent
      label={"Charts"}
      expand={expandCharts}
      setExpand={setExpandCharts}
    >
      <TabContext value={selectedChart}>
        <Conditional condition={Tabs.length > 1}>
          <ToggleV2
            className={styles.twoItemToggleButton}
            selected={selectedChart}
            toggleData={Tabs}
            onPressed={setSelectedChart}
          />
        </Conditional>

        <TabPanel value="Asset Allocation">
          <AssetAllocationPieChart useColorMappingsV1={true} data={chartData} />
        </TabPanel>

        <TabPanel value="Projected Returns">
          <ProjectedReturnsGraph />
        </TabPanel>
      </TabContext>
    </ItemWithExpandCollapseContent>
  );
};

export default PortfolioDetailsSideBarChartsSection;
